'use client';

import { Subsidiary } from '@coolblue-development/constants';
import { Box, Button, Flex, freddyTheme, SpriteIcon } from '@coolblue-development/freddy';
import closeCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/closeCollectionTrigger';
import confirmTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/confirmTrigger';
import openCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/openCollectionTrigger';
import showCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/showCollectionTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { AvailabilityStates } from '@coolblue-development/next-availability-state';
import { AvailabilityState as ClientAvailabilityState } from '@coolblue-development/next-availability-state/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import { MobileEntrance } from '@features/product/components/Collection/MobileEntrance';
import { MobileModal } from '@features/product/components/Collection/MobileModal';
import { useGetCollectionTitle } from '@hooks/collectionHooks';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import {
  getCurrentProductValues,
  matchPropertyValues,
  shouldShowCollection
} from '@utils/collectionUtils';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import {
  CollectionInitialProduct,
  type CollectionProps,
  CollectionType,
  type Price,
  type Product,
  Variant
} from './Collection.types';
import { DropdownCollectionMobile } from './DropdownCollection/DropdownCollection.Mobile';
import { PropertyCollection } from './PropertyCollection';

const VALUE_SEPARATOR = '|';

export const MobileCollection = ({ collection, mainImageId, product }: CollectionProps) => {
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const { isMobile } = useBreakpoints();
  const { t } = useTranslation('webshop.productPage');
  const { isInternalUser, subsidiary } = useRequestContext();

  const getListPriceForProduct = (
    listPrice: Price | null | undefined,
    prices?: { msrp?: Price }
  ) => {
    return subsidiary === Subsidiary.DE && prices?.msrp?.includingVat ? prices.msrp : listPrice;
  };

  const initialSelectedProduct: CollectionInitialProduct = {
    availabilityState: product.availabilityState,
    price: product.salesPrice.includingVat,
    listPrice: getListPriceForProduct(product?.listPrice, product?.prices)?.includingVat,
    redirectUrl: '',
    id: product.id,
    personalPrice: product.personalPrice,
    brandName: product.brand?.name
  };

  const [isOpen, setIsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(initialSelectedProduct);

  const handleProductClick = (product: Product | null) => {
    if (product && product.nullableAvailabilityState && product.nullableSalesPrice) {
      setSelectedProduct({
        availabilityState: product.nullableAvailabilityState,
        price: product.nullableSalesPrice.includingVat,
        listPrice: getListPriceForProduct(product?.listPrice, product?.prices)?.includingVat,
        redirectUrl: product.urls.product || '',
        id: product.id || 0,
        personalPrice: product.personalPrice ?? undefined,
        brandName: product.brand?.name
      });
    }
  };

  const collectionTitle = useGetCollectionTitle(collection.title, product.productType);

  const mappedCollection = matchPropertyValues(collection, selectedProduct?.id);

  if (!shouldShowCollection(mappedCollection)) return <></>;

  const collectionProductIds = mappedCollection.discriminants[0].values
    .flatMap(value => value.products)
    .filter(product => product?.id)
    .map(product => product?.id);

  const getAvailabilityStateProps = (availabilityState: string) => {
    switch (availabilityState) {
      case AvailabilityStates.OnStock:
        return {
          promise: t('onStock'),
          state: {
            color: freddyTheme.color.font.sentimentPositiveContrast,
            icon: (
              <SpriteIcon
                name='checkmark-circled'
                color={freddyTheme.color.icon.sentimentPositiveContrast}
              />
            )
          }
        };
      case AvailabilityStates.AvailableSoon:
        return {
          promise: t('availableSoon'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      case AvailabilityStates.PreOrder:
        return {
          promise: t('preOrder'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      case AvailabilityStates.OutOfStock:
        return {
          promise: t('permanentlyOutOfStock'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      default:
        return {
          promise: t('temporaryOutOfStock'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
    }
  };

  const handleConfirmButtonClick = () => {
    setIsOpen(false);

    const shouldRedirect =
      selectedProduct.redirectUrl &&
      selectedProduct.redirectUrl !== initialSelectedProduct.redirectUrl;
    if (shouldRedirect) {
      router.push(selectedProduct.redirectUrl);
    }

    trackEvent(
      confirmTrigger({
        sub_feature:
          mappedCollection.type === CollectionType.DROPDOWN
            ? 'drop_down_selector'
            : 'multi_discriminant_selector',
        index: 1,
        total_indices: 1,
        item_id: selectedProduct.id
      })
    );
  };

  const handleOpenModal = () => {
    setIsOpen(true);
    trackEvent(
      openCollectionTrigger({
        index: 1,
        item_list_id: product?.id ?? 0,
        item_list_name: product?.name ?? '',
        number_of_items: collectionProductIds.length,
        total_indices: 1,
        sub_feature:
          mappedCollection.type === CollectionType.DROPDOWN
            ? 'drop_down_selector'
            : 'multi_discriminant_selector'
      })
    );
  };

  const closeCollectionTracking = closeCollectionTrigger({
    sub_feature:
      mappedCollection.type === CollectionType.DROPDOWN
        ? 'drop_down_selector'
        : 'multi_discriminant_selector'
  });

  return (
    <Box hideFrom='md' {...(isInternalUser && { 'data-cy': 'mobile-collection' })}>
      {isMobile && (
        <TrackImmediate
          trigger={showCollectionTrigger({
            sub_feature:
              mappedCollection.type === CollectionType.DROPDOWN
                ? 'drop_down_selector'
                : 'multi_discriminant_selector',
            item_ids: collectionProductIds.toString()
          })}
        />
      )}
      <MobileEntrance
        collectionTitle={collectionTitle}
        currentValues={getCurrentProductValues(collection)}
        handleOpenModal={handleOpenModal}
        isInternalUser={isInternalUser}
        productType={product.productType}
        valueSeparator={VALUE_SEPARATOR}
      />
      <MobileModal<CollectionInitialProduct>
        trackingEvent={closeCollectionTracking}
        collectionTitle={collectionTitle}
        initialSelectedProduct={initialSelectedProduct}
        isInternalUser={isInternalUser}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        setSelectedProduct={setSelectedProduct}
        modalBody={
          <>
            {collection.type === CollectionType.DROPDOWN ? (
              <DropdownCollectionMobile
                collection={mappedCollection}
                onProductClick={handleProductClick}
                selectedProductId={selectedProduct?.id}
              />
            ) : (
              <PropertyCollection
                collection={mappedCollection}
                mainImageId={mainImageId}
                onProductClick={handleProductClick}
                product={product}
                variant={Variant.MOBILE}
                selectedProductId={selectedProduct?.id}
              />
            )}
          </>
        }
        modalFooter={
          <Flex gap={200} sx={{ width: '100%' }}>
            <Flex sx={{ flexGrow: 1 }} direction='column' gap={200}>
              {selectedProduct.price && (
                <SalesPrice
                  currentPrice={<SalesPriceCurrent price={selectedProduct.price} />}
                  personalPrice={selectedProduct.personalPrice}
                  hidePersonalPriceLabel
                  {...(selectedProduct.listPrice && {
                    formerPrice: (
                      <SalesPriceFormer
                        price={selectedProduct.listPrice}
                        prefix={t('salesPrice.prefix')}
                        brandName={selectedProduct.brandName}
                      />
                    )
                  })}
                />
              )}
              {selectedProduct.availabilityState && (
                <ClientAvailabilityState
                  {...getAvailabilityStateProps(selectedProduct.availabilityState)}
                />
              )}
            </Flex>
            <Button sx={{ flexGrow: 1 }} onClick={handleConfirmButtonClick}>
              {t('collection.confirmButton')}
            </Button>
          </Flex>
        }
      />
    </Box>
  );
};
